/**
 * Elation reports → the library's imaging picker.
 *
 * This is the adapter between the two halves of the imaging feature: the
 * host-side read (`elationReportsRead.ts`, which knows Elation) and
 * `@astrana/ui`'s `ImagingStudy` (which knows nothing about any EHR). It is a
 * pure function of already-scrubbed data — it performs no read, no injection
 * and no I/O, and it never alters narrative text.
 *
 * ─── THE ONE RULE ───────────────────────────────────────────────────────────
 *
 * WHEN THIS FILE CANNOT VOUCH FOR ITS OWN SECTION SPLIT, THE PHYSICIAN GETS THE
 * WHOLE REPORT VERBATIM. Never a subset, never silence.
 *
 * That is the invariant every branch below serves, and it exists because the
 * failure it prevents is the worst one available here. A review constructed a
 * combined two-body-part study — "XR CHEST 2 VIEWS; XR RIGHT KNEE 3 VIEWS", which
 * Elation carries as ONE narrative with `FINDINGS:` and `IMPRESSION:` appearing
 * TWICE. An earlier version of this file took the first match of each label and,
 * because both fields were then populated, suppressed the whole-report fallback.
 * The knee vanished: a comminuted tibial plateau fracture never reached the note,
 * and what did reach it read as a clean chest film. No marker, no disabled row,
 * no count — the physician had no way to know anything was missing.
 *
 * So: ambiguity routes to `fullText`. Concatenating duplicate sections was the
 * other option and was rejected — joining them means writing a separator no
 * radiologist wrote, and `fullText` invents nothing.
 *
 * ─── IT MAPS. IT DOES NOT SPREAD ────────────────────────────────────────────
 *
 * The two sides use DIFFERENT KEYS for the same three-state verdict:
 *
 *     host read  →  grid.narrative.status   "clean" | "scrubbed" | "unsafe"
 *     library    →  study.availability      "clean" | "scrubbed" | "unsafe"
 *
 * The VALUES are identical, which is what makes a spread dangerous rather than
 * merely wrong: `{...grid}` type-checks against nothing here and produces a study
 * whose `availability` is undefined. The library reads `availability` through an
 * allowlist, so every study comes back unselectable and the dropdown goes 100%
 * inert — no error, no empty state, no log line. It fails closed, which is the
 * right direction, and invisibly, which is the dangerous one.
 *
 * There is a sharper trap on the same object: `ReportGridRead` has its OWN
 * `status` field (`getStatus()`, e.g. `"FINAL"`) one level above the narrative's
 * verdict. Spreading the grid therefore does not leave `availability` undefined —
 * it fills it with `"FINAL"`, a plausible-looking string that is not in the
 * allowlist. The picker still refuses everything, but a reader debugging it sees a
 * populated field and concludes the mapping works.
 *
 * ─── THIS SEAM IS A TRUST BOUNDARY, SO IT VALIDATES ─────────────────────────
 *
 * The typed signature is a promise the caller cannot keep. `imagingReports`
 * arrives as a CAST of `chrome.runtime` message JSON that nothing validates, and
 * the extension bundles `extension/elation` and is released SEPARATELY from the web shell —
 * so an extension build older or newer than the deployed app is a realistic
 * source of shapes these types forbid. An earlier version threw a `TypeError` on
 * a missing `narrative`/`sections`/`grids`, and because the caller maps inside a
 * `useMemo` during render, the nearest boundary is the app-level `ErrorBoundary`:
 * a malformed payload would unmount the entire Member 360 tree and take the
 * physician's in-progress draft with it. Every field is therefore read
 * defensively, and an unrecognised verdict is classified `unsafe` HERE rather
 * than left to the library's allowlist to catch downstream.
 *
 * ─── WHAT THIS DELIBERATELY DOES NOT DO ─────────────────────────────────────
 *
 *   • It does not scrub. The narrative arrives scrubbed and its verdict arrives
 *     with it; a second opinion here would be about text it cannot see.
 *   • It does not trim, re-wrap or re-punctuate copied text. `trim()` appears
 *     only as an emptiness TEST and for display-only labels.
 *   • It does not invent a date. `ElationReportRead` carries no performed-on
 *     field, so `performedOn` is omitted rather than guessed from something
 *     adjacent — a study labelled with the wrong date is worse than one with none.
 *   • It does not invent a modality, and does not parse one out of a title.
 *
 * One study per GRID, not per report: the narrative hangs off a grid
 * (`getLabReportNote()`), so collapsing to one study per report would have to pick
 * one grid's narrative and drop the rest.
 */
import type { ImagingStudy } from "@astrana/ui/scribe";

import type {
  ElationReportRead,
  ReportGridRead,
  ReportNarrative,
  ReportNarrativeSection,
} from "@ext/elation/elationReportsRead";
import type { UnsafeReason } from "@ext/elation/resultTextUrlScrub";

/**
 * THE VERDICTS THAT PERMIT TEXT TO BE COPIED. An allowlist, and the only one.
 *
 * Deliberately a `Set<string>` keyed by the union's members: the typed union is a
 * compile-time promise, and the input to this file is untyped JSON. `"UNSAFE"`
 * differs from `"unsafe"` only in case — and uppercase enums ARE this codebase's
 * house style elsewhere (`EMR_ELATION`, `FINAL`, `IMAGING`) — so a case-sensitive
 * check for equality with `"unsafe"` let that value fall through to the AVAILABLE
 * branch and copy narrative text out beside a verdict meaning "the scrub
 * refused". Nothing reached the note, but only because a second module's
 * allowlist caught it. This file decides the verdict; it must not outsource that.
 */
const TEXT_BEARING_VERDICTS: ReadonlySet<string> = new Set<
  ReportNarrative["status"]
>(["clean", "scrubbed"]);

/**
 * Every verdict, enumerated so the compiler polices this file.
 *
 * If `elationReportsRead` grows a fourth state, this record stops compiling —
 * verified by mutation: adding a `"partially-scrubbed"` arm upstream produced two
 * type errors, both here.
 */
const EXHAUSTIVE_VERDICTS: Record<ReportNarrative["status"], true> = {
  clean: true,
  scrubbed: true,
  unsafe: true,
};

/**
 * The headings the physician named: "pull impressions and findings section".
 *
 * Whole-label matches, never prefixes — a prefix rule would let `FINDINGS AND
 * IMPRESSION` land in `findings` with its impression half unrepresented.
 * `EXAMINATION` is never pulled into the note; it names the study for the row
 * (see `studyTitle`).
 *
 * No mixed-case label can reach these sets: the upstream parser's heading rule
 * requires all-caps, so `Findings:` yields no sections at all and the narrative
 * arrives unsplit — which routes to `fullText` and is safe. `toUpperCase()` below
 * is belt-and-braces for untyped input, not mixed-case support.
 */
const FINDINGS_LABELS: ReadonlySet<string> = new Set(["FINDINGS"]);
const IMPRESSION_LABELS: ReadonlySet<string> = new Set([
  "IMPRESSION",
  "IMPRESSIONS",
]);
const EXAMINATION_LABELS: ReadonlySet<string> = new Set(["EXAMINATION", "EXAM"]);

/**
 * What the clinician reads when text could not be vouched for.
 *
 * A sentence for a physician, not an error code: the library renders this
 * verbatim in a disabled row. Every `UnsafeReason` maps to one, exhaustively, so
 * no raw enum can surface — and they all say the same thing on purpose. The
 * difference between "url cut mid-parameter" and "glued prose inside a query" is
 * meaningful to us and meaningless to her; what she needs is that the report
 * EXISTS, was not pulled, and the reason is our check rather than a missing study.
 */
const UNAVAILABLE_SENTENCE =
  "Report text could not be verified as safe to pull — open it in the chart.";

const UNAVAILABLE_REASONS: Record<UnsafeReason, string> = {
  "url-cut-mid-parameter": UNAVAILABLE_SENTENCE,
  "query-text-outside-the-span": UNAVAILABLE_SENTENCE,
  "glued-prose-inside-a-query": UNAVAILABLE_SENTENCE,
  "glued-prose-inside-an-authority": UNAVAILABLE_SENTENCE,
  "glued-token-is-not-a-word": UNAVAILABLE_SENTENCE,
  "broken-url-start": UNAVAILABLE_SENTENCE,
};

/** Defensive: the narrative's sections, or none, whatever the payload claims. */
function sectionsOf(
  narrative: ReportNarrative | undefined,
): ReportNarrativeSection[] {
  if (!narrative || !("sections" in narrative)) return [];
  const sections = (narrative as { sections?: unknown }).sections;
  return Array.isArray(sections) ? (sections as ReportNarrativeSection[]) : [];
}

/** Defensive: the narrative's scrubbed text, or "" when it carries none. */
function textOf(narrative: ReportNarrative | undefined): string {
  if (!narrative || !("text" in narrative)) return "";
  const text = (narrative as { text?: unknown }).text;
  return typeof text === "string" ? text : "";
}

/**
 * Whether the sections can be trusted to describe `text`.
 *
 * `ReportNarrativeSection` carries `start`/`end` precisely so byte-identity
 * against the scrubbed narrative is checkable rather than merely promised. This
 * checks it. A section whose slice does not equal its `text` means the two
 * disagree about what the scrub vouched for, and the honest response is to stop
 * splitting and hand over the vouched-for whole.
 *
 * Unreachable from today's producer, which always slices. That is the point: it
 * converts an upstream promise into a locally verified fact, for free.
 */
function sectionsDescribeText(
  text: string,
  sections: readonly ReportNarrativeSection[],
): boolean {
  return sections.every((section) => {
    if (typeof section?.text !== "string") return false;
    if (typeof section.start !== "number" || typeof section.end !== "number") {
      return false;
    }
    return text.slice(section.start, section.end) === section.text;
  });
}

/** A label lookup's outcome. `ambiguous` is the case that must not be silent. */
type SectionPick =
  | { readonly kind: "none" }
  | { readonly kind: "one"; readonly text: string }
  | { readonly kind: "ambiguous"; readonly occurrences: number };

const NO_SECTION: SectionPick = { kind: "none" };

/**
 * Find a named section, and REPORT AMBIGUITY rather than resolving it.
 *
 * Counting instead of returning the first match is the fix for the dropped-knee
 * defect in the header. Two `FINDINGS:` headings mean this narrative covers more
 * than one thing, and no single-value field can carry that.
 *
 * A whitespace-only body counts as absent, not as text: the library treats a
 * present-but-empty string as insertable, and inserting nothing under a heading
 * she chose is the empty-insertion failure its own guard exists to prevent.
 */
function pickSection(
  sections: readonly ReportNarrativeSection[],
  labels: ReadonlySet<string>,
): SectionPick {
  const matches = sections.filter((section) => {
    const label = typeof section?.label === "string" ? section.label : "";
    return labels.has(label.trim().toUpperCase());
  });
  if (matches.length > 1) {
    return { kind: "ambiguous", occurrences: matches.length };
  }
  const withText = matches.filter(
    (section) => typeof section.text === "string" && section.text.trim(),
  );
  if (withText.length === 1) return { kind: "one", text: withText[0].text };
  return NO_SECTION;
}

/**
 * What the physician reads to tell one row from another.
 *
 * `reportTypeName` alone is "Imaging Report" for EVERY imaging study on the
 * chart, so a head CT and an abdomen CT rendered as two identical rows and the
 * only way to pick the right one was luck. The fix copies, never derives:
 *
 *   1. the `EXAMINATION` section — the radiologist's own study name, present on
 *      both live sandbox narratives — verbatim, trimmed FOR DISPLAY only;
 *   2. `accessionNumber`, appended when present, which distinguishes two studies
 *      of the same body part on the same day.
 *
 * Falls back through `reportTypeName` → `reportTypeRaw` → a constant, because a
 * blank row is one she cannot identify at all.
 */
function studyTitle(
  report: ElationReportRead,
  examination: SectionPick,
  grid: ReportGridRead | undefined,
): string {
  const named =
    (examination.kind === "one" ? examination.text.trim() : "") ||
    (typeof report.reportTypeName === "string"
      ? report.reportTypeName.trim()
      : "") ||
    (typeof report.reportTypeRaw === "string" ? report.reportTypeRaw.trim() : "") ||
    "Imaging report";

  const accession =
    typeof grid?.accessionNumber === "string" ? grid.accessionNumber.trim() : "";
  return accession ? `${named} · ${accession}` : named;
}

/** The unavailable study — it exists, it is visible, and it carries no text. */
function unavailableStudy(
  id: string,
  title: string,
  narrative: ReportNarrative | undefined,
): ImagingStudy {
  const reason =
    narrative?.status === "unsafe"
      ? UNAVAILABLE_REASONS[narrative.reason]
      : undefined;
  return {
    id,
    title,
    availability: "unsafe",
    unavailableReason: reason ?? UNAVAILABLE_SENTENCE,
  };
}

/**
 * One grid → one study. Every field assigned; no spread of grid, narrative or
 * report appears anywhere in this function, and that is deliberate.
 */
function studyFromGrid(
  report: ElationReportRead,
  grid: ReportGridRead | undefined,
  gridIndex: number,
  position: number,
): ImagingStudy {
  // Unique by CONSTRUCTION, not by trusting Elation's id hygiene. `position` is
  // the study's index in the emitted list, so two reports arriving with the same
  // id — or with none — still produce distinct rows. The library keys selection
  // on this, so a collision means ticking one row inserts another's narrative.
  const id = `${position}:${report.id ?? "unknown"}:${gridIndex}`;
  const narrative: ReportNarrative | undefined = grid?.narrative;

  const sections = sectionsOf(narrative);
  const text = textOf(narrative);
  const sectionsTrusted = sectionsDescribeText(text, sections);
  const title = studyTitle(
    report,
    sectionsTrusted ? pickSection(sections, EXAMINATION_LABELS) : NO_SECTION,
    grid,
  );

  // THE VERDICT, read off `narrative.status` BY NAME — not `grid.status`
  // ("FINAL"), not a spread of either — and allowlisted here rather than
  // downstream. Anything unrecognised, including a case variant of a known
  // value, is unavailable.
  const verdict = narrative?.status;
  if (typeof verdict !== "string" || !TEXT_BEARING_VERDICTS.has(verdict)) {
    return unavailableStudy(id, title, narrative);
  }
  const availability: "clean" | "scrubbed" =
    verdict === "clean" ? "clean" : "scrubbed";

  const findingsPick = sectionsTrusted
    ? pickSection(sections, FINDINGS_LABELS)
    : NO_SECTION;
  const impressionPick = sectionsTrusted
    ? pickSection(sections, IMPRESSION_LABELS)
    : NO_SECTION;

  // THE ONE RULE. A repeated heading — or sections that do not describe the text
  // — means this file cannot say which words belong to which study, so it hands
  // over the whole vouched-for narrative instead of a confident subset.
  const ambiguous =
    findingsPick.kind === "ambiguous" || impressionPick.kind === "ambiguous";

  const findings =
    !ambiguous && findingsPick.kind === "one" ? findingsPick.text : undefined;
  const impression =
    !ambiguous && impressionPick.kind === "one" ? impressionPick.text : undefined;

  // `fullText` is the fallback for a narrative that could not be split, and only
  // that: where the sections CAN be named, those are what she asked for, and
  // sending both would offer her the same paragraphs twice.
  const whole = text.trim() ? text : undefined;
  const fullText =
    findings === undefined && impression === undefined ? whole : undefined;

  return {
    id,
    title,
    availability,
    ...(findings === undefined ? {} : { findings }),
    ...(impression === undefined ? {} : { impression }),
    ...(fullText === undefined ? {} : { fullText }),
  };
}

/**
 * Every imaging study on the chart, in the order the read produced them.
 *
 * Order is preserved rather than sorted: `all()` ordering is not stable between
 * calls on the live page, so any order invented here would be no more meaningful
 * and would cost the caller the ability to correlate the two.
 *
 * DELETED REPORTS ARE EXCLUDED, and a payload that cannot vouch that a report is
 * live is treated as deleted. `if (report.deleted)` read "the producer did not
 * tell me" as "not deleted", so `deleted: 0` or a missing field kept the report —
 * failing OPEN on the one check where a false negative reintroduces content a
 * practice retracted from the medical record. Requiring an explicit boolean is
 * the right asymmetry: dropping a live study is visible and recoverable,
 * surfacing a withdrawn one is neither.
 *
 * CONFIDENTIAL REPORTS ARE INCLUDED, deliberately. `isConfidential()` marks a
 * report restricted within Elation's sharing model; it is still this patient's
 * chart, the clinician reading it here is the one drafting the note in front of
 * it, and nothing is inserted without her selecting it. Hiding it would hand her
 * an incomplete study list with no indication anything was withheld.
 *
 * `expectedPatientId` is an OPTIONAL SECOND LOCK on the patient guard. The
 * primary lock is upstream — `buildElationReports` is called with the id from the
 * tab URL and drops every disagreeing report — and this repeats it at the point
 * of use, where this repo has a history of stale-payload and patient-switch bugs.
 * When omitted, nothing is dropped: a fail-closed default here would blank the
 * picker for every caller that does not yet pass it, which is the invisible-inert
 * failure this file works hardest to avoid.
 */
export function imagingStudiesFromReports(
  reports: readonly ElationReportRead[],
  expectedPatientId?: string,
): ImagingStudy[] {
  // Referenced so the exhaustiveness record cannot be dropped as unused.
  void EXHAUSTIVE_VERDICTS;

  const studies: ImagingStudy[] = [];
  if (!Array.isArray(reports)) return studies;

  const expected = expectedPatientId?.trim();

  for (const report of reports) {
    if (!report) continue;
    if (report.kind !== "imaging") continue;
    if (typeof report.deleted !== "boolean" || report.deleted) continue;
    if (expected && String(report.patientId ?? "").trim() !== expected) continue;

    const grids: readonly (ReportGridRead | undefined)[] = Array.isArray(
      report.grids,
    )
      ? report.grids
      : [];
    grids.forEach((grid, gridIndex) => {
      studies.push(studyFromGrid(report, grid, gridIndex, studies.length));
    });
  }
  return studies;
}
